import { ReactNode, useEffect } from "react";
import Home from "./Home/Home";
import About from "./About";
import Projects from "./Project";
import Experience from "./Experience";
import Contact from "./Contact";
import Section from "../components/Section/Section";
import ProgressRail from "../components/ProgressRail/ProgressRail";
import StorySlot from "../components/Story/StorySlot";
import { usePhoneScene, useStoryMode } from "../hooks/useStoryMode";
import { goToSection, useSectionFlow } from "../lib/sectionFlow";

export default function OnePage() {
    // In story mode every section is a scene that pins itself (SceneTrack), so
    // the StorySlot wrapper — which pins and fades whole sections against each
    // other — is skipped. Wrapping them too would fade a section's second
    // project out before the reader reached it.
    const story = useStoryMode();
    // A phone held upright gets portrait versions of the same scenes.
    const phoneScene = usePhoneScene();
    // Scenes draw their own marker and heartbeat inside the pinned stage.
    const scene = story || phoneScene;
    // Scenes move section to section like slides (see lib/sectionFlow).
    useSectionFlow(scene);
    const slot = (node: ReactNode, opts?: { first?: boolean; last?: boolean }) =>
        scene ? node : <StorySlot {...opts}>{node}</StorySlot>;

    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (!hash) return;
        const el = document.getElementById(hash);
        if (!el) return;
        // One frame of slack so the sections have laid out before we measure.
        const id = setTimeout(() => {
            if (!goToSection(hash, { instant: true })) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        return () => clearTimeout(id);
    }, []);

    return (
        <main>
            <ProgressRail />

            {slot(
                <Section id="home" beat={!scene}>
                    <Home />
                </Section>,
                { first: true }
            )}

            {slot(
                <Section id="about" beat={!scene} tint="74, 190, 214" index={2} label="Profile" hideMarker={scene}>
                    <About />
                </Section>
            )}

            {slot(
                <Section id="experience" beat={!scene} tint="150, 220, 120" index={3} label="Experience" hideMarker={scene}>
                    <Experience />
                </Section>
            )}

            {slot(
                <Section id="projects" beat={!scene} tint="124, 146, 255" index={4} label="Work" hideMarker={scene}>
                    <Projects />
                </Section>
            )}

            {slot(
                <Section id="contact" beat={!scene} tint="255, 206, 160" index={5} label="Contact" hideMarker={scene}>
                    <Contact />
                </Section>,
                { last: true }
            )}
        </main>
    );
}
